/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import React from 'react';

/**
 * AttributeSkeleton - Skeleton for a single product card attribute
 */
const AttributeSkeleton: React.FC = () => (
  <div className="flex min-h-px min-w-px flex-1 shrink-0 flex-col items-start justify-center gap-spacing/gap/soft-6px rounded-radius/card-inner">
    <div className="skeleton h-4 w-full shrink-0 rounded-radius/button md:w-[171px]" />
    <div className="skeleton h-5 w-27 shrink-0 rounded-radius/button" />
  </div>
);

/**
 * SectionButtonSkeleton - Skeleton for a single section button
 */
const SectionButtonSkeleton: React.FC = () => (
  <div className="box-border flex w-full shrink-0 flex-col items-start gap-spacing/gap/soft-6px px-spacing/padding/soft-12px py-0">
    <div className="skeleton box-border flex h-[34px] w-full shrink-0 items-center justify-between overflow-hidden rounded-radius/button px-spacing/padding/soft-12px py-spacing/gap/soft-12px md:h-9" />
  </div>
);

/**
 * ProductCardSkeleton - Loading skeleton for ProductCard component
 * Based on Figma design with Ant Design-style shimmer animation
 *
 * @design Figma: TI - Jul - Sep 2025
 * - XL: node-id 17001-41204 (1440px+)
 * - LG: node-id 17001-41444 (1280px)
 * - MD: node-id 17001-42847 (768px)
 * - SM: node-id 17005-44808 (360px)
 * @note Uses arbitrary values to match exact pixel specifications from Figma design
 */
export const ProductCardSkeleton: React.FC = () => {
  return (
    <div
      className="relative size-full overflow-hidden rounded-radius/card border border-solid border-borders/default bg-fill/card"
      data-testid="product-card-skeleton"
    >
      <div className="flex size-full flex-col items-start overflow-hidden rounded-radius/card">
        {/* Top Section - Provider Logo, Name, Rating, CTA */}
        <div className="box-border flex w-full shrink-0 flex-col items-start gap-spacing/gap/y px-spacing/padding/soft-12px pb-0 pt-spacing/padding/soft-12px md:pb-spacing/gap/soft-12px">
          <div className="flex w-full shrink-0 items-center gap-spacing/gap/soft-12px md:gap-spacing/gap/x">
            {/* Provider Logo */}
            <div className="skeleton h-18 w-28 shrink-0 rounded-radius/tooltip md:h-[97px] md:w-38" />

            {/* Name and Rating */}
            <div className="flex min-h-px min-w-px flex-1 shrink-0 flex-col items-start justify-center gap-1 md:gap-spacing/gap/soft-6px">
              <div className="skeleton h-[22px] w-full shrink-0 rounded-radius/button md:max-w-[410px]" />
              <div className="skeleton h-[22px] w-[107px] shrink-0 rounded-radius/button md:h-5 md:w-[171px]" />
            </div>

            {/* Right CTA Section - Hidden on mobile, shown on md+ */}
            <div className="hidden w-general/2-col-width shrink-0 flex-col items-center justify-between gap-1 md:flex">
              <div className="skeleton h-5 w-[82px] shrink-0 rounded-radius/button" />
              <div className="skeleton h-button/size w-full shrink-0 rounded-radius/button" />
              <div className="skeleton h-5 w-[114px] shrink-0 rounded-radius/button" />
            </div>
          </div>{' '}
          {/* Mobile CTA Section - Shown only on mobile */}
          <div className="flex w-full shrink-0 items-center gap-2 md:hidden">
            <div className="flex min-h-px min-w-px flex-1 shrink-0 flex-col items-start justify-center gap-1">
              <div className="skeleton h-5 w-[82px] shrink-0 rounded-radius/button" />
              <div className="skeleton h-5 w-[114px] shrink-0 rounded-radius/button" />
            </div>
            <div className="skeleton h-[30px] min-h-px min-w-px flex-1 shrink-0 rounded-radius/button" />
          </div>
        </div>

        {/* Card Attributes Section */}
        <div className="box-border flex w-full shrink-0 flex-col items-start gap-spacing/gap/soft-12px px-spacing/padding/soft-12px py-spacing/padding/y md:pb-spacing/gap/y md:pt-0">
          {/* Attributes Grid - 2x2 on mobile, 1x4 on md+ */}
          <div className="flex w-full shrink-0 flex-col gap-spacing/gap/soft-12px md:flex-row md:items-start md:gap-spacing/gap/x md:rounded-radius/card-inner">
            {/* First row on mobile / First two attributes on desktop */}
            <div className="flex w-full shrink-0 gap-spacing/gap/x md:contents">
              <AttributeSkeleton />
              <AttributeSkeleton />
            </div>

            {/* Second row on mobile / Last two attributes on desktop */}
            <div className="flex w-full shrink-0 gap-spacing/gap/x md:contents">
              <AttributeSkeleton />
              <AttributeSkeleton />
            </div>
          </div>
        </div>

        {/* Bottom Section - Action Buttons */}
        <div className="box-border flex w-full shrink-0 flex-col items-start justify-center gap-spacing/gap/soft-6px overflow-hidden pb-spacing/padding/soft-12px pt-0 bg-fill/card">
          <SectionButtonSkeleton />
          <SectionButtonSkeleton />
          <SectionButtonSkeleton />
        </div>
      </div>
    </div>
  );
};
